import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProvidedAsset,
  UserAssetConstraints,
  getUserAssetConstraints,
  uploadUserAsset,
} from '../services/projectAssets';
import AssetUploadForm, { AssetUpload } from './AssetUploadForm';

interface CreateProjectAssetsProps {
  value: ProvidedAsset[];
  onChange: (assets: ProvidedAsset[]) => void;
  enabled: boolean;
}

export default function CreateProjectAssets({ value, onChange, enabled }: CreateProjectAssetsProps) {
  const { t } = useTranslation(['common']);
  const [constraints, setConstraints] = useState<UserAssetConstraints | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (enabled) {
      getUserAssetConstraints().then(setConstraints).catch(() => {});
    }
  }, [enabled]);

  const handleUpload = async ({ name, description, file, options }: AssetUpload) => {
    const uploaded = await uploadUserAsset(name, description, file, options);
    onChange([...value, { assetId: uploaded.assetId, name, description }]);
    setIsAdding(false);
  };

  return (
    <div>
      <label
        style={{
          display: 'block',
          font: '500 12px/1 var(--font-sans)',
          color: 'var(--text-2)',
          marginBottom: 6,
        }}
      >
        {t('assets.createTitle', 'Your assets (optional)')}
      </label>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
        {enabled
          ? t(
              'assets.createHint',
              'Add your own images, fonts, sound effects and music. Entire generates images for you, but using your own saves credits and keeps your style. Give each asset a unique name and describe what it is for.',
            )
          : t('assets.signInHint', 'Sign in first to add your own assets.')}
      </div>

      {value.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {value.map((asset) => (
            <div
              key={asset.assetId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                border: '1px solid var(--line-2)',
                borderRadius: 10,
                background: 'var(--surface)',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <span className="mono" style={{ fontSize: 13 }}>/{asset.name}</span>
                <span
                  className="muted"
                  style={{ fontSize: 12, marginLeft: 10 }}
                  title={asset.description}
                >
                  {asset.description.length > 60
                    ? `${asset.description.slice(0, 60)}…`
                    : asset.description}
                </span>
              </div>
              <button
                type="button"
                className="btn sm"
                onClick={() => onChange(value.filter((a) => a.assetId !== asset.assetId))}
                title={t('assets.remove', 'Remove')}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {isAdding ? (
        <AssetUploadForm
          constraints={constraints}
          existingNames={value.map((asset) => asset.name)}
          onUpload={handleUpload}
          onCancel={() => setIsAdding(false)}
        />
      ) : (
        <button
          type="button"
          className="btn sm"
          onClick={() => setIsAdding(true)}
          disabled={
            !enabled || (constraints != null && value.length >= constraints.maxAssetsPerProject)
          }
        >
          {t('assets.add', '+ Add asset')}
        </button>
      )}
    </div>
  );
}
